import React from 'react';
import ActivateTargetAPI from '../../../containers/activate-target/ActivateTargetAPI';
import { TARGET_TYPES } from '../../../containers/activate-target/models/target-type';
import { isLegacyActivateCurrentTeam } from '../../../core/activate-legacy-teams';
import { GlobalSearchItem, GlobalSearchRegistryItem } from '../../interfaces/global-search';
import { GenericListItemContent, GenericListItemContentProps } from './GenericListItemContent/GenericListItemContent';
import { GlobalSearchEntityCommonData } from './interfaces/global-search-entity';

export const GLOBAL_SEARCH_TARGET_KEY = 'targets';

/**
 * Targets in global search follow the same 1.0/2.0 fork as the `/target/*` route, so a legacy team
 * never sees 2.0 data behind the 1.0 UI (or vice versa). Both paths emit the same
 * `/target/{id}/overview` exit URL, which `DashboardRoutes` resolves to the matching version.
 * Resolved per call rather than at module load, so a fresh login is picked up without a reload. The
 * gate itself pins the version for the session, so resolving late here cannot put this registry on a
 * different version from the mounted route.
 */
export const GlobalSearchTargetRegistry: GlobalSearchRegistryItem<GlobalSearchEntityCommonData> = {
  key: GLOBAL_SEARCH_TARGET_KEY,
  title: 'Targets',
  maxItemsWhenNoFiltersApplied: 2,
  sortOrder: 5,
  filters: [
    {
      value: GLOBAL_SEARCH_TARGET_KEY,
      label: 'Targets'
    }
  ],
  viewAllRoute: '/activation?tab=target',
  inMemoryFiltering: true,
  dataResolver: () => (isLegacyActivateCurrentTeam() ? legacyTargetsResolver() : activate2TargetsResolver()),
  renderItem: (item) => {
    const props: GenericListItemContentProps = {
      suffixIcon: item.icon,
      primaryText: item.name,
      secondaryText: item.data.displayType,
      primaryLogoProps: {
        logoURL: item.data.logoUrl,
        darkModeLogoURL: item.data.darkModeLogoUrl,
        // 1.0 target types carry brand colours in the static catalog; 2.0 types are backend-driven
        // and have none, so these resolve to undefined on that path.
        primaryColor: TARGET_TYPES[item.data.type]?.primaryColor,
        darkModePrimaryColor: TARGET_TYPES[item.data.type]?.darkModePrimaryColor
      },
      primaryLogoTooltip: item.data.displayType
    };

    return (
      <GenericListItemContent {...props} />
    );
  }
};

/** Activate 1.0 (in-ETL reverse-ETL under `/api/config`) — unchanged from pre-migration behaviour. */
function legacyTargetsResolver() {
  return ActivateTargetAPI.getTargets().then(res => {
    const targets = res.data.map(GlobalSearchTargetFactory);
    return [...targets];
  });
}

/**
 * Activate 2.0 (standalone activate-service) — joins the plugin-gated type catalog for display.
 *
 * Reached through a dynamic `import()` so this eager registry module carries no static
 * `src/react/activate/**` import. `DashboardRoutes` already lazy-loads the Activate 2.0 route tree;
 * importing the 2.0 service and factory here pulled them into an eager chunk regardless of whether
 * the team is on 2.0 at all. Only a legacy team's search now never fetches this chunk.
 */
function activate2TargetsResolver() {
  return import('../../../activate/targets/global-search/activate2TargetsResolver').then(module =>
    module.activate2TargetsResolver()
  );
}

export function GlobalSearchTargetFactory(
  rawTarget: any
): GlobalSearchItem<GlobalSearchEntityCommonData> {
  return {
    data: {
      name: rawTarget.name,
      type: rawTarget.type,
      displayType: rawTarget.type_display_name,
      logoUrl: rawTarget.type_logo_url,
      darkModeLogoUrl: rawTarget.type_dark_mode_logo_url ?? rawTarget.type_logo_url,
    },
    key: `${rawTarget.type}-target-${rawTarget.seq_id}`,
    name: rawTarget.name,
    exitURL: `/target/${rawTarget.seq_id}/overview`,
    icon: 'target',
    searchKeywords: [rawTarget.name, rawTarget.type, rawTarget.type_display_name]
  };
}

/*
 * `GlobalSearchActivate2TargetFactory` moved to
 * `activate/targets/global-search/activate2TargetsResolver` — it maps a 2.0 `Target`, so keeping it
 * here would reintroduce the static import of the 2.0 interface this module now avoids.
 */
